'use client';

import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  Menu,
  Text,
  Input,
  IconButton,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { Portal } from '@ark-ui/react';
import { Search01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  FINDING_MAIN_CONTENT_TAB_TYPE,
  type Tab,
} from '../../../store/layoutStore';
import { MENU_NAVIGATION_KEYS } from '../../../constants/keyboardKeys';
import { getMainContentTabIcon } from '../tabIcons';
import { useTargetType } from '../../../store/projectStore';
import { useFindingEditStore } from '../../../store/findingEditStore';
import { resolveOverviewTabTitle } from '../utils';

const MENU_GAP_PX = 4;

type TabSearchMenuProps = {
  tabs: Tab[];
  onSelectTab: (tabId: string) => void;
};

export function TabSearchMenu({ tabs, onSelectTab }: TabSearchMenuProps) {
  const t = useTranslations('layout');
  const targetType = useTargetType();
  const findingEditStates = useFindingEditStore(
    (state) => state.findingEditStates,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const homeLabel = t('home');
  const targetOverviewLabel = t('targetOverview');
  const resolveTitle = (tab: Tab) =>
    resolveOverviewTabTitle({
      tabId: tab.id,
      title: tab.title,
      tabType: tab.type,
      targetType,
      home: homeLabel,
      targetOverview: targetOverviewLabel,
    });
  const normalizedQuery = query.trim().toLowerCase();
  const filteredTabs = useMemo(() => {
    if (!normalizedQuery) {
      return tabs;
    }

    return tabs.filter((tab) =>
      resolveOverviewTabTitle({
        tabId: tab.id,
        title: tab.title,
        tabType: tab.type,
        targetType,
        home: homeLabel,
        targetOverview: targetOverviewLabel,
      })
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [tabs, normalizedQuery, targetType, homeLabel, targetOverviewLabel]);

  return (
    <Menu.Root
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-end',
        gutter: MENU_GAP_PX,
        flip: false,
        slide: false,
        overlap: false,
        overflowPadding: 0,
        getAnchorRect: () => {
          const trigger = triggerRef.current;

          if (!trigger) {
            return null;
          }

          const rect = trigger.getBoundingClientRect();

          return {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
          };
        },
        updatePosition: async ({ updatePosition, floatingElement }) => {
          await updatePosition();
          const trigger = triggerRef.current;

          if (!trigger || !floatingElement) {
            return;
          }

          const rect = trigger.getBoundingClientRect();

          floatingElement.style.setProperty(
            '--x',
            `${Math.round(rect.right - floatingElement.offsetWidth)}px`,
          );
          floatingElement.style.setProperty(
            '--y',
            `${Math.round(rect.bottom + MENU_GAP_PX)}px`,
          );
        },
      }}
      onOpenChange={(details) => {
        if (!details.open) setQuery('');
      }}
      onSelect={(details) => {
        onSelectTab(details.value);
      }}
    >
      <Tooltip title={t('searchTabsTooltip')}>
        <Menu.Trigger asChild>
          <IconButton
            ref={triggerRef}
            variant="ghost"
            size="xs"
            aria-label={t('searchTabsTooltip')}
            color="fg.neutral.muted"
          >
            <Search01Icon size={16} strokeWidth={1.75} />
          </IconButton>
        </Menu.Trigger>
      </Tooltip>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            minW="320px"
            w="320px"
            p="2"
            gap="1"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border.neutral.subtle"
            bg="bg.neutral.default"
          >
            <Box px="2" py="1">
              <Box position="relative">
                <Box
                  position="absolute"
                  left="2"
                  top="50%"
                  transform="translateY(-50%)"
                  color="fg.neutral.muted"
                  display="flex"
                  alignItems="center"
                  pointerEvents="none"
                  zIndex={1}
                >
                  <Search01Icon size={16} strokeWidth={1.75} />
                </Box>
                <Input
                  size="sm"
                  value={query}
                  autoFocus
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                    if (!MENU_NAVIGATION_KEYS.has(event.key)) {
                      event.stopPropagation();
                    }
                  }}
                  placeholder={t('searchTabs')}
                  aria-label={t('searchTabs')}
                  pl="9"
                  h="8"
                  borderWidth="1px"
                  borderColor="border.neutral.default"
                  borderRadius="sm"
                  bg="bg.neutral.default"
                  color="fg.neutral.default"
                  _placeholder={{ color: 'fg.neutral.muted' }}
                  _hover={{ borderColor: 'border.neutral.default' }}
                  _focus={{
                    borderColor: 'border.neutral.default',
                    outline: 'none',
                    boxShadow: 'none',
                  }}
                  _focusVisible={{
                    borderColor: 'border.neutral.default',
                    outline: 'none',
                    boxShadow: 'none',
                  }}
                />
              </Box>
            </Box>
            {filteredTabs.length > 0 ? (
              <Menu.ItemGroup maxH="320px" overflowY="auto">
                <Menu.ItemGroupLabel px="2">
                  <Text textStyle="label" color="fg.neutral.muted">
                    {t('openTabs')}
                  </Text>
                </Menu.ItemGroupLabel>
                {filteredTabs.map((tab) => (
                  <Menu.Item key={tab.id} value={tab.id}>
                    <HStack gap="2" alignItems="center" minW="0" w="full">
                      <Box
                        flexShrink={0}
                        w="3.5"
                        color="fg.neutral.default"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {getMainContentTabIcon(tab.type, {
                          size: 14,
                          mode:
                            tab.type === FINDING_MAIN_CONTENT_TAB_TYPE
                              ? (findingEditStates[tab.data?.findingId]?.mode ??
                                tab.data?.mode)
                              : undefined,
                        })}
                      </Box>
                      <Text textStyle="body" truncate>
                        {resolveTitle(tab)}
                      </Text>
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.ItemGroup>
            ) : (
              <Box px="3" py="3" textAlign="center">
                <Text textStyle="body" color="fg.neutral.muted">
                  {t('noTabsFound')}
                </Text>
              </Box>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
